import React from 'react';

// API/Components
import {
	isEmpty,
	validValue,
	formatDate,
	formatSecondsToTime
} from './enums';

// Material UI
import Backdrop from '@mui/material/Backdrop';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';

// set the width of the left column in the table
const leftColumnWidth = '45%';

export default function SystemStatsSummary({ systemAnalysisData, loading = false }) {

	// Default table data structure
	let tableData = [{
		recentEvents: 'Unknown',
		recentDischarges: 'Unknown',
		recentDischargeTime: 'Unknown',
		avgUnitAge: 'Unknown',
		lastRestring: '--/--/----',
		numUnitsReplaced: '--',
		percentUnitsReplaced: '--',
		numUnitsOutOfWarranty: '--',
		percentUnitsOutOfWarranty: '--'
	}];

	if (systemAnalysisData !== undefined && !isEmpty(systemAnalysisData)) {
		const recentEvents = validValue(systemAnalysisData.analysis.stats.recent_events, "Unknown");
		const recentDischarges = validValue(systemAnalysisData.analysis.stats.recent_discharges.number, "Unknown");
		const recentDischargeTime = validValue(systemAnalysisData.analysis.stats.recent_discharges.total_time, "Unknown");
		const avgUnitAge = validValue(systemAnalysisData.analysis.stats.avg_unit_age, "Unknown");
		const lastRestring = formatDate(systemAnalysisData.analysis.stats.last_restring, '--/--/----');
		const numUnitsReplaced = validValue(systemAnalysisData.analysis.stats.num_units_replaced.number ?? 0);
		const percentUnitsReplaced = validValue(systemAnalysisData.analysis.stats.num_units_replaced.percentage ?? 0);
		const numUnitsOutOfWarranty = validValue(systemAnalysisData.analysis.stats.num_units_out_of_warranty, 0);
		const percentUnitsOutOfWarranty = numUnitsOutOfWarranty / (systemAnalysisData.info.num_strings * systemAnalysisData.info.num_units_per_string) * 100;

		tableData = ({
			recentEvents: recentEvents,
			recentDischarges: recentDischarges,
			recentDischargeTime: formatSecondsToTime(recentDischargeTime),
			avgUnitAge: avgUnitAge,
			lastRestring: lastRestring,
			numUnitsReplaced: numUnitsReplaced,
			percentUnitsReplaced: percentUnitsReplaced,
			numUnitsOutOfWarranty: numUnitsOutOfWarranty,
			percentUnitsOutOfWarranty: percentUnitsOutOfWarranty
		});
	}

	return (
		<Card style={{ border: '1px solid #D8D7D6', borderRadius: '5px', boxShadow: '2px 2px 4px gray', px: 0 }}>
			<Box sx={{ backgroundColor: 'lightgray', p: 1 }}>
				<Typography sx={{ textAlign: 'center', fontFamily: 'Saira,sans-serif', fontWeight: 'bold' }}>
					System Stats Summary
				</Typography>
			</Box>
			<CardContent style={{ padding: 0, position: 'relative' }}>
				{loading && (
					<Backdrop
						sx={{
							color: '#fff',
							zIndex: (theme) => theme.zIndex.drawer + 1,
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							height: '100%',
							borderRadius: 'inherit',
							backgroundColor: 'rgba(0,0,0,0.5)'
						}}
						open={true}
					>
						<CircularProgress color="inherit" />
					</Backdrop>
				)}
				<TableContainer>
					<Table>
						<TableBody>
							<TableRow>
								<TableCell width={leftColumnWidth} sx={{ p: 1 }}>Recent Events</TableCell>
								<TableCell sx={{ p: 1 }}>{tableData.recentEvents} events</TableCell>
							</TableRow>
							<TableRow>
								<TableCell width={leftColumnWidth} sx={{ p: 1 }}>Recent Discharges</TableCell>
								<TableCell sx={{ p: 1 }}>
									{tableData.recentDischarges} events (Total time: {tableData.recentDischargeTime})
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell width={leftColumnWidth} sx={{ p: 1 }}>Average Unit Age</TableCell>
								<TableCell sx={{ p: 1 }}>{tableData.avgUnitAge} years</TableCell>
							</TableRow>
							<TableRow>
								<TableCell width={leftColumnWidth} sx={{ p: 1 }}>Last Restring</TableCell>
								<TableCell sx={{ p: 1 }}>{tableData.lastRestring}</TableCell>
							</TableRow>
							<TableRow>
								<TableCell width={leftColumnWidth} sx={{ p: 1 }}>Units Replaced</TableCell>
								<TableCell sx={{ p: 1 }}>{tableData.numUnitsReplaced} ({tableData.percentUnitsReplaced}%)</TableCell>
							</TableRow>
							<TableRow>
								<TableCell width={leftColumnWidth} sx={{ p: 1 }}>Units Out of Warranty</TableCell>
								<TableCell sx={{ p: 1 }}>{tableData.numUnitsOutOfWarranty} ({tableData.percentUnitsOutOfWarranty}%)</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			</CardContent>
		</Card>
	);
}
